import type { MessageDescriptor } from 'react-intl';

import type { CollabGraphRecommendedEntity } from '../../../utils/collaborationUsers';
import type { OrgMetricsResponse } from '../../../utils/orgMetrics';

import { messages } from './messages';

/**
 * Identifier for a static org-metrics dataset bundled with the debug panel.
 *
 * These datasets mirror rows from the "Sample metrics by organisation"
 * Confluence page (`6858084748`) and are used by the debug panel to seed
 * `OrgMetricsAtom` without hitting the live `metrics/org/{orgId}` endpoint.
 */
export type StaticDatasetKey =
	| 'accenture'
	| 'allegion'
	| 'atlassian'
	| 'communardo'
	| 'reddit'
	| 'procoreLargest'
	| 'helloFresh'
	| 'workday'
	| 'workdayLargest';

/**
 * UI-facing description of a single static dataset.
 */
export type StaticDataset = {
	key: StaticDatasetKey;
	label: MessageDescriptor;
	description: MessageDescriptor;
	metrics: OrgMetricsResponse;
};

const COMPUTED_AT = '2026-04-23T07:54:06.392+00:00';

/**
 * Static datasets sourced from Confluence page 6858084748 ("Sample metrics by
 * organisation"). Numeric strings from the source table have been parsed to
 * numbers, `-` has been mapped to `null`, and obvious source typos (e.g.
 * "2026-04-1", "167, 966", "18, 519") have been normalised to their intended
 * values.
 *
 * `cacheStatus`, `daysSinceSnapshot`, `estimatedNodesNow`, `commentsDataAsOf`,
 * `meetingNotesDataAsOf`, `connectorDataAsOf`, `smartlinkDataAsOf`,
 * `forgeAppsInstalled`, and per-user fields (`entitiesTouched`, `collaborators`,
 * `collaboratorTeams`, `collaboratorTeamsTruncated`) are not part of the source
 * table — they are derived/operational fields supplied by the BE and are left
 * as `null` here.
 */
export const STATIC_DATASETS: Record<StaticDatasetKey, StaticDataset> = {
	accenture: {
		key: 'accenture',
		label: messages.datasetAccentureLabel,
		description: messages.datasetAccentureDescription,
		metrics: {
			organisationId: 'a53d6b63-3k57-1408-j79d-0b60b08kb685',
			totalRelationships: 107366741,
			activeRelTypes: 235,
			jiraCodeLinks: 1136203,
			goalWorkItemLinks: 4919,
			relDataAsOf: '2026-04-18',
			computedAt: COMPUTED_AT,
			cacheStatus: null,
			avgDailyRelChange: 643225,
			avgRelChangePerSecond: 7.4447,
			avgDailyJiraCodeLinkChange: 1107,
			avgJiraCodeLinkChangePerSecond: 0.0128,
			totalJiraIssues: 4844413,
			avgArtifactsPerIssue: 0.7,
			totalNodes: 24456310,
			activeNodeTypes: 71,
			teamCount: 13750,
			userCount: 12358,
			nodeDataAsOf: '2026-04-18',
			avgDailyNodeGrowth: 70638,
			avgNodeGrowthPerSecond: 0.8176,
			daysSinceSnapshot: null,
			estimatedNodesNow: null,
			activeContributors30d: 0,
			activityDataAsOf: '2026-04-17',
			avgFetchQueriesPerDay: 1421,
			avgFetchQueriesPerSecond: 0.0164,
			queryDataAsOf: null,
			pagesCreated30d: 167966,
			pagesDataAsOf: '2026-04-18',
			loomsRecorded30d: 2,
			totalMinutesRecorded: 32,
			meetingMinutesSaved: 48,
			loomDataAsOf: '2026-04-21T12:14:21.319+00:00',
			issuesMovedForward7d: 1855,
			issueActivityDataAsOf: '2026-04-21',
			commentsCreated7d: 29,
			commentsDataAsOf: null,
			meetingNotesWithRecording7d: 0,
			meetingNotesDataAsOf: null,
			connectorObjects30d: 790,
			connectorDataAsOf: null,
			connectorObjectsByProvider: [{ providerName: 'Slack provider', objects30d: 719 }, { providerName: 'Figma', objects30d: 71 }],
			smartlinkPastes30d: 0,
			smartlinkDataAsOf: null,
			connectorsInstalled: 0,
			connectorsActive28d: 0,
			pctConnectorsActive28d: 0,
			onePAppsInstalled: 13,
			marketplaceAddonsInstalled: 292,
			threePConnectorsInstalled: 0,
			forgeAppsInstalled: null,
			totalAppsInstalled: 305,
			connectorsDetail: null,
			entitiesTouched: null,
			collaborators: null,
			collaboratorTeams: null,
			collaboratorTeamsTruncated: null,
		},
	},
	allegion: {
		key: 'allegion',
		label: messages.datasetAllegionLabel,
		description: messages.datasetAllegionDescription,
		metrics: {
			organisationId: 'b588d5e7-4ecd-4761-ba49-53e2027dea76',
			totalRelationships: 1369930,
			activeRelTypes: 152,
			jiraCodeLinks: 31049,
			goalWorkItemLinks: 0,
			relDataAsOf: '2026-04-19',
			computedAt: COMPUTED_AT,
			cacheStatus: null,
			avgDailyRelChange: 13365,
			avgRelChangePerSecond: 0.1547,
			avgDailyJiraCodeLinkChange: 312,
			avgJiraCodeLinkChangePerSecond: 0.0036,
			totalJiraIssues: 17952,
			avgArtifactsPerIssue: 2.8,
			totalNodes: 315970,
			activeNodeTypes: 51,
			teamCount: 98,
			userCount: 138,
			nodeDataAsOf: '2026-04-18',
			avgDailyNodeGrowth: 1426,
			avgNodeGrowthPerSecond: 0.0165,
			daysSinceSnapshot: null,
			estimatedNodesNow: null,
			activeContributors30d: 1,
			activityDataAsOf: '2026-04-19',
			avgFetchQueriesPerDay: 6834,
			avgFetchQueriesPerSecond: 0.0791,
			queryDataAsOf: '2026-04-19',
			pagesCreated30d: 30399,
			pagesDataAsOf: '2026-04-19',
			loomsRecorded30d: 0,
			totalMinutesRecorded: 0,
			meetingMinutesSaved: 0,
			loomDataAsOf: null,
			issuesMovedForward7d: 462,
			issueActivityDataAsOf: '2026-04-18',
			commentsCreated7d: 664,
			commentsDataAsOf: null,
			meetingNotesWithRecording7d: 0,
			meetingNotesDataAsOf: null,
			connectorObjects30d: 107702,
			connectorDataAsOf: null,
			connectorObjectsByProvider: [{ providerName: 'GitHub for Jira', objects30d: 106757 }, { providerName: 'Figma', objects30d: 589 }, { providerName: 'Azure Devops provider', objects30d: 243 }, { providerName: 'Mural', objects30d: 79 }, { providerName: 'Bitbucket Pipelines Integration', objects30d: 15 }, { providerName: 'Gitlab provider', objects30d: 19 }],
			smartlinkPastes30d: 0,
			smartlinkDataAsOf: null,
			connectorsInstalled: 0,
			connectorsActive28d: 0,
			pctConnectorsActive28d: 0,
			onePAppsInstalled: 8,
			marketplaceAddonsInstalled: 4,
			threePConnectorsInstalled: 0,
			forgeAppsInstalled: null,
			totalAppsInstalled: 12,
			connectorsDetail: null,
			entitiesTouched: null,
			collaborators: null,
			collaboratorTeams: null,
			collaboratorTeamsTruncated: null,
		},
	},
	atlassian: {
		key: 'atlassian',
		label: messages.datasetAtlassianLabel,
		description: messages.datasetAtlassianDescription,
		metrics: {
			organisationId: '2346a038-3c8c-498b-a79b-e7847859868d',
			totalRelationships: 4465869234,
			activeRelTypes: 356,
			jiraCodeLinks: 56152610,
			goalWorkItemLinks: 41083,
			relDataAsOf: '2026-04-18',
			computedAt: COMPUTED_AT,
			cacheStatus: null,
			avgDailyRelChange: 10361407,
			avgRelChangePerSecond: 119.9237,
			avgDailyJiraCodeLinkChange: 206668,
			avgJiraCodeLinkChangePerSecond: 2.392,
			totalJiraIssues: 1739733,
			avgArtifactsPerIssue: 37.1,
			totalNodes: 1642078616,
			activeNodeTypes: 96,
			teamCount: 26522,
			userCount: 46491,
			nodeDataAsOf: '2026-04-18',
			avgDailyNodeGrowth: 9897860,
			avgNodeGrowthPerSecond: 114.5586,
			daysSinceSnapshot: null,
			estimatedNodesNow: null,
			activeContributors30d: 7902,
			activityDataAsOf: '2026-04-18',
			avgFetchQueriesPerDay: 6019720,
			avgFetchQueriesPerSecond: 69.6727,
			queryDataAsOf: '2026-04-18',
			pagesCreated30d: 5068524,
			pagesDataAsOf: '2026-04-18',
			loomsRecorded30d: 132785,
			totalMinutesRecorded: 2394194,
			meetingMinutesSaved: 3591291,
			loomDataAsOf: '2026-04-22T23:59:51.278+00:00',
			issuesMovedForward7d: 317494,
			issueActivityDataAsOf: '2026-04-18',
			commentsCreated7d: 2258042,
			commentsDataAsOf: null,
			meetingNotesWithRecording7d: 1527,
			meetingNotesDataAsOf: null,
			connectorObjects30d: 9608,
			connectorDataAsOf: null,
			connectorObjectsByProvider: [{ providerName: 'Google Drive provider', objects30d: 7392 }, { providerName: 'Slack provider', objects30d: 1740 }, { providerName: 'Salesforce', objects30d: 441 }, { providerName: 'Docusign provider', objects30d: 31 }, { providerName: 'Bitbucket Pipelines Integration', objects30d: 4 }],
			smartlinkPastes30d: 52,
			smartlinkDataAsOf: null,
			connectorsInstalled: 15,
			connectorsActive28d: 11,
			pctConnectorsActive28d: 73.3,
			onePAppsInstalled: 9,
			marketplaceAddonsInstalled: 5,
			threePConnectorsInstalled: 15,
			forgeAppsInstalled: null,
			totalAppsInstalled: 29,
			connectorsDetail: [{ connectorName: 'workday', connectorCategory: 'rovo_3p', pctUsersAuthed28d: 0.0, isUnderutilised: true, globalPctOrgsWithAnyAuthedUser: 0.0 }],
			entitiesTouched: null,
			collaborators: null,
			collaboratorTeams: null,
			collaboratorTeamsTruncated: null,
		},
	},
	communardo: {
		key: 'communardo',
		label: messages.datasetCommunardoLabel,
		description: messages.datasetCommunardoDescription,
		metrics: {
			organisationId: '3cc38732-6e1a-46de-9520-6365c14cc4ca',
			totalRelationships: 25013683,
			activeRelTypes: 206,
			jiraCodeLinks: 85239,
			goalWorkItemLinks: 160,
			relDataAsOf: '2026-04-18',
			computedAt: COMPUTED_AT,
			cacheStatus: null,
			avgDailyRelChange: 115491,
			avgRelChangePerSecond: 1.3367,
			avgDailyJiraCodeLinkChange: 94,
			avgJiraCodeLinkChangePerSecond: 0.0011,
			totalJiraIssues: 31694,
			avgArtifactsPerIssue: 7.1,
			totalNodes: 8072099,
			activeNodeTypes: 66,
			teamCount: 76,
			userCount: 680,
			nodeDataAsOf: '2026-04-18',
			avgDailyNodeGrowth: 1732,
			avgNodeGrowthPerSecond: 0.02,
			daysSinceSnapshot: null,
			estimatedNodesNow: null,
			activeContributors30d: 31,
			activityDataAsOf: '2026-04-18',
			avgFetchQueriesPerDay: 29473,
			avgFetchQueriesPerSecond: 0.3411,
			queryDataAsOf: '2026-04-18',
			pagesCreated30d: 5287,
			pagesDataAsOf: '2026-04-18',
			loomsRecorded30d: 92,
			totalMinutesRecorded: 3564,
			meetingMinutesSaved: 5346,
			loomDataAsOf: '2026-04-17T14:36:42.367+00:00',
			issuesMovedForward7d: 6075,
			issueActivityDataAsOf: '2026-04-18',
			commentsCreated7d: 5143,
			commentsDataAsOf: null,
			meetingNotesWithRecording7d: 4,
			meetingNotesDataAsOf: null,
			connectorObjects30d: 2338235,
			connectorDataAsOf: null,
			connectorObjectsByProvider: [{ providerName: 'OneDrive provider', objects30d: 1106642 }, { providerName: 'Sharepoint provider', objects30d: 630301 }, { providerName: 'MS Teams provider', objects30d: 550977 }, { providerName: 'Bitbucket Pipelines Integration', objects30d: 24752 }, { providerName: 'Google Drive provider', objects30d: 20483 }],
			smartlinkPastes30d: 0,
			smartlinkDataAsOf: null,
			connectorsInstalled: 3,
			connectorsActive28d: 2,
			pctConnectorsActive28d: 66.7,
			onePAppsInstalled: 11,
			marketplaceAddonsInstalled: 60,
			threePConnectorsInstalled: 3,
			forgeAppsInstalled: null,
			totalAppsInstalled: 74,
			connectorsDetail: null,
			entitiesTouched: null,
			collaborators: null,
			collaboratorTeams: null,
			collaboratorTeamsTruncated: null,
		},
	},
	reddit: {
		key: 'reddit',
		label: messages.datasetRedditLabel,
		description: messages.datasetRedditDescription,
		metrics: {
			organisationId: '9d6f1fce-a96f-4999-9afb-7c1c9b6ee40b',
			totalRelationships: 77679572,
			activeRelTypes: 215,
			jiraCodeLinks: 1553778,
			goalWorkItemLinks: 562,
			relDataAsOf: '2026-04-18',
			computedAt: COMPUTED_AT,
			cacheStatus: null,
			avgDailyRelChange: 169455,
			avgRelChangePerSecond: 1.9613,
			avgDailyJiraCodeLinkChange: 3618,
			avgJiraCodeLinkChangePerSecond: 0.0419,
			totalJiraIssues: 96065,
			avgArtifactsPerIssue: 21.2,
			totalNodes: 23338742,
			activeNodeTypes: 65,
			teamCount: 257,
			userCount: 447,
			nodeDataAsOf: '2026-04-18',
			avgDailyNodeGrowth: 33617,
			avgNodeGrowthPerSecond: 0.3891,
			daysSinceSnapshot: null,
			estimatedNodesNow: null,
			activeContributors30d: 40,
			activityDataAsOf: '2026-04-18',
			avgFetchQueriesPerDay: 256704,
			avgFetchQueriesPerSecond: 2.9711,
			queryDataAsOf: '2026-04-18',
			pagesCreated30d: 1278,
			pagesDataAsOf: '2026-04-18',
			loomsRecorded30d: 1941,
			totalMinutesRecorded: 6911,
			meetingMinutesSaved: 10366,
			loomDataAsOf: '2026-04-18T00:34:13.240+00:00',
			issuesMovedForward7d: 18519,
			issueActivityDataAsOf: '2026-04-18',
			commentsCreated7d: 18871,
			commentsDataAsOf: null,
			meetingNotesWithRecording7d: 0,
			meetingNotesDataAsOf: null,
			connectorObjects30d: 14156619,
			connectorDataAsOf: null,
			connectorObjectsByProvider: [{ providerName: 'Zendesk provider', objects30d: 7859462 }, { providerName: 'Google Drive provider', objects30d: 4533543 }, { providerName: 'Google Calendar', objects30d: 997478 }, { providerName: 'GitHub for Jira', objects30d: 545570 }, { providerName: 'Slack provider', objects30d: 200175 }],
			smartlinkPastes30d: 2,
			smartlinkDataAsOf: null,
			connectorsInstalled: 7,
			connectorsActive28d: 6,
			pctConnectorsActive28d: 85.7,
			onePAppsInstalled: 11,
			marketplaceAddonsInstalled: 28,
			threePConnectorsInstalled: 7,
			forgeAppsInstalled: null,
			totalAppsInstalled: 46,
			connectorsDetail: null,
			entitiesTouched: null,
			collaborators: null,
			collaboratorTeams: null,
			collaboratorTeamsTruncated: null,
		},
	},
	procoreLargest: {
		key: 'procoreLargest',
		label: messages.datasetProcoreLargestLabel,
		description: messages.datasetProcoreLargestDescription,
		metrics: {
			organisationId: 'd1de2e90-c8b6-4598-9400-85329abae8a8',
			totalRelationships: 152234806,
			activeRelTypes: 272,
			jiraCodeLinks: 3481503,
			goalWorkItemLinks: 2444,
			relDataAsOf: '2026-04-19',
			computedAt: COMPUTED_AT,
			cacheStatus: null,
			avgDailyRelChange: 253452,
			avgRelChangePerSecond: 2.9335,
			avgDailyJiraCodeLinkChange: 7253,
			avgJiraCodeLinkChangePerSecond: 0.0839,
			totalJiraIssues: 287928,
			avgArtifactsPerIssue: 15.3,
			totalNodes: 40400747,
			activeNodeTypes: 77,
			teamCount: 1734,
			userCount: 771,
			nodeDataAsOf: '2026-04-18',
			avgDailyNodeGrowth: 36653,
			avgNodeGrowthPerSecond: 0.4242,
			daysSinceSnapshot: null,
			estimatedNodesNow: null,
			activeContributors30d: 50,
			activityDataAsOf: '2026-04-19',
			avgFetchQueriesPerDay: 397534,
			avgFetchQueriesPerSecond: 4.6011,
			queryDataAsOf: '2026-04-19',
			pagesCreated30d: 5299,
			pagesDataAsOf: '2026-04-19',
			loomsRecorded30d: 5006,
			totalMinutesRecorded: 51856,
			meetingMinutesSaved: 77785,
			loomDataAsOf: '2026-04-22T23:48:47.695+00:00',
			issuesMovedForward7d: 42805,
			issueActivityDataAsOf: '2026-04-21',
			commentsCreated7d: 31176,
			commentsDataAsOf: null,
			meetingNotesWithRecording7d: 31,
			meetingNotesDataAsOf: null,
			connectorObjects30d: 8642100,
			connectorDataAsOf: null,
			connectorObjectsByProvider: [{ providerName: 'Google Drive provider', objects30d: 4509208 }, { providerName: 'GitHub for Jira', objects30d: 2735347 }, { providerName: 'Workday', objects30d: 1315602 }, { providerName: 'Loom', objects30d: 51897 }, { providerName: 'Slack provider', objects30d: 11053 }],
			smartlinkPastes30d: 2,
			smartlinkDataAsOf: null,
			connectorsInstalled: 4,
			connectorsActive28d: 2,
			pctConnectorsActive28d: 50,
			onePAppsInstalled: 9,
			marketplaceAddonsInstalled: 46,
			threePConnectorsInstalled: 4,
			forgeAppsInstalled: null,
			totalAppsInstalled: 59,
			connectorsDetail: null,
			entitiesTouched: null,
			collaborators: null,
			collaboratorTeams: null,
			collaboratorTeamsTruncated: null,
		},
	},
	helloFresh: {
		key: 'helloFresh',
		label: messages.datasetHelloFreshLabel,
		description: messages.datasetHelloFreshDescription,
		metrics: {
			organisationId: 'bbf8f438-d361-4796-8c68-08df9b370626',
			totalRelationships: 66735838,
			activeRelTypes: 182,
			jiraCodeLinks: 2130875,
			goalWorkItemLinks: 2,
			relDataAsOf: '2026-04-18',
			computedAt: COMPUTED_AT,
			cacheStatus: null,
			avgDailyRelChange: 63107,
			avgRelChangePerSecond: 0.7304,
			avgDailyJiraCodeLinkChange: 3415,
			avgJiraCodeLinkChangePerSecond: 0.0395,
			totalJiraIssues: 186994,
			avgArtifactsPerIssue: 14.9,
			totalNodes: 12155494,
			activeNodeTypes: 53,
			teamCount: 1427,
			userCount: 634,
			nodeDataAsOf: '2026-04-18',
			avgDailyNodeGrowth: 12374,
			avgNodeGrowthPerSecond: 0.1432,
			daysSinceSnapshot: null,
			estimatedNodesNow: null,
			activeContributors30d: 5,
			activityDataAsOf: '2026-04-18',
			avgFetchQueriesPerDay: 153295,
			avgFetchQueriesPerSecond: 1.7742,
			queryDataAsOf: '2026-04-18',
			pagesCreated30d: 1579,
			pagesDataAsOf: '2026-04-18',
			loomsRecorded30d: 0,
			totalMinutesRecorded: 0,
			meetingMinutesSaved: 0,
			loomDataAsOf: null,
			issuesMovedForward7d: 24590,
			issueActivityDataAsOf: '2026-04-18',
			commentsCreated7d: 17560,
			commentsDataAsOf: null,
			meetingNotesWithRecording7d: 1,
			meetingNotesDataAsOf: null,
			connectorObjects30d: 2016164,
			connectorDataAsOf: null,
			connectorObjectsByProvider: [{ providerName: 'GitHub for Jira', objects30d: 1862699 }, { providerName: 'Google Drive provider', objects30d: 144973 }, { providerName: 'Slack provider', objects30d: 4873 }, { providerName: 'Figma', objects30d: 2512 }, { providerName: 'Miro provider', objects30d: 649 }],
			smartlinkPastes30d: 0,
			smartlinkDataAsOf: null,
			connectorsInstalled: 1,
			connectorsActive28d: 0,
			pctConnectorsActive28d: 0,
			onePAppsInstalled: 7,
			marketplaceAddonsInstalled: 47,
			threePConnectorsInstalled: 1,
			forgeAppsInstalled: null,
			totalAppsInstalled: 55,
			connectorsDetail: null,
			entitiesTouched: null,
			collaborators: null,
			collaboratorTeams: null,
			collaboratorTeamsTruncated: null,
		},
	},
	workday: {
		key: 'workday',
		label: messages.datasetWorkdayLabel,
		description: messages.datasetWorkdayDescription,
		metrics: {
			organisationId: 'f98b223b-a1fb-4b98-8b5c-81a6b3360896',
			totalRelationships: 7445978,
			activeRelTypes: 158,
			jiraCodeLinks: 400310,
			goalWorkItemLinks: 0,
			relDataAsOf: '2026-04-19',
			computedAt: COMPUTED_AT,
			cacheStatus: null,
			avgDailyRelChange: 14226,
			avgRelChangePerSecond: 0.1647,
			avgDailyJiraCodeLinkChange: 781,
			avgJiraCodeLinkChangePerSecond: 0.009,
			totalJiraIssues: 43146,
			avgArtifactsPerIssue: 17.8,
			totalNodes: 1889990,
			activeNodeTypes: 48,
			teamCount: 202,
			userCount: 303,
			nodeDataAsOf: '2026-04-18',
			avgDailyNodeGrowth: 1942,
			avgNodeGrowthPerSecond: 0.0225,
			daysSinceSnapshot: null,
			estimatedNodesNow: null,
			activeContributors30d: 1,
			activityDataAsOf: '2026-04-19',
			avgFetchQueriesPerDay: 118663,
			avgFetchQueriesPerSecond: 1.3734,
			queryDataAsOf: '2026-04-19',
			pagesCreated30d: 174,
			pagesDataAsOf: '2026-04-18',
			loomsRecorded30d: 0,
			totalMinutesRecorded: 0,
			meetingMinutesSaved: 0,
			loomDataAsOf: null,
			issuesMovedForward7d: 13336,
			issueActivityDataAsOf: '2026-04-19',
			commentsCreated7d: 8315,
			commentsDataAsOf: null,
			meetingNotesWithRecording7d: 0,
			meetingNotesDataAsOf: null,
			connectorObjects30d: 140593,
			connectorDataAsOf: null,
			connectorObjectsByProvider: [{ providerName: 'GitHub for Jira', objects30d: 81019 }, { providerName: 'Google Drive provider', objects30d: 56964 }, { providerName: '__unknown__', objects30d: 2196 }, { providerName: 'Figma', objects30d: 390 }, { providerName: 'Version Related Links app', objects30d: 12 }],
			smartlinkPastes30d: 0,
			smartlinkDataAsOf: null,
			connectorsInstalled: 0,
			connectorsActive28d: 0,
			pctConnectorsActive28d: 0,
			onePAppsInstalled: 7,
			marketplaceAddonsInstalled: 23,
			threePConnectorsInstalled: 0,
			forgeAppsInstalled: null,
			totalAppsInstalled: 30,
			connectorsDetail: null,
			entitiesTouched: null,
			collaborators: null,
			collaboratorTeams: null,
			collaboratorTeamsTruncated: null,
		},
	},
	workdayLargest: {
		key: 'workdayLargest',
		label: messages.datasetWorkdayLargestLabel,
		description: messages.datasetWorkdayLargestDescription,
		metrics: {
			organisationId: 'c175835a-1029-4b03-bda0-601b1a101710',
			totalRelationships: 20547334,
			activeRelTypes: 192,
			jiraCodeLinks: 1128522,
			goalWorkItemLinks: 422,
			relDataAsOf: '2026-04-19',
			computedAt: COMPUTED_AT,
			cacheStatus: null,
			avgDailyRelChange: 34648,
			avgRelChangePerSecond: 0.401,
			avgDailyJiraCodeLinkChange: 1457,
			avgJiraCodeLinkChangePerSecond: 0.0169,
			totalJiraIssues: 21202,
			avgArtifactsPerIssue: 59.7,
			totalNodes: 3185817,
			activeNodeTypes: 52,
			teamCount: 101,
			userCount: 67,
			nodeDataAsOf: '2026-04-18',
			avgDailyNodeGrowth: 4857,
			avgNodeGrowthPerSecond: 0.0562,
			daysSinceSnapshot: null,
			estimatedNodesNow: null,
			activeContributors30d: 4,
			activityDataAsOf: '2026-04-21',
			avgFetchQueriesPerDay: 75641,
			avgFetchQueriesPerSecond: 0.8755,
			queryDataAsOf: '2026-04-21',
			pagesCreated30d: 449,
			pagesDataAsOf: '2026-04-21',
			loomsRecorded30d: 0,
			totalMinutesRecorded: 0,
			meetingMinutesSaved: 0,
			loomDataAsOf: null,
			issuesMovedForward7d: 4785,
			issueActivityDataAsOf: '2026-04-21',
			commentsCreated7d: 5043,
			commentsDataAsOf: null,
			meetingNotesWithRecording7d: 0,
			meetingNotesDataAsOf: null,
			connectorObjects30d: 610847,
			connectorDataAsOf: null,
			connectorObjectsByProvider: [{ providerName: 'GitHub for Jira', objects30d: 558225 }, { providerName: 'Google Drive provider', objects30d: 46406 }, { providerName: 'Figma', objects30d: 4864 }, { providerName: 'Slack provider', objects30d: 1294 }, { providerName: 'Lucid provider', objects30d: 35 }],
			smartlinkPastes30d: 0,
			smartlinkDataAsOf: null,
			connectorsInstalled: 1,
			connectorsActive28d: 0,
			pctConnectorsActive28d: 0,
			onePAppsInstalled: 9,
			marketplaceAddonsInstalled: 31,
			threePConnectorsInstalled: 1,
			forgeAppsInstalled: null,
			totalAppsInstalled: 41,
			connectorsDetail: null,
			entitiesTouched: null,
			collaborators: null,
			collaboratorTeams: null,
			collaboratorTeamsTruncated: null,
		},
	},
};

/**
 * Ordered list of static datasets, used by the debug panel UI to render the
 * selectable list in the order Atlassian designers laid them out on the
 * source Confluence page.
 */
export const STATIC_DATASET_LIST: ReadonlyArray<StaticDataset> = [
	STATIC_DATASETS.accenture,
	STATIC_DATASETS.allegion,
	STATIC_DATASETS.atlassian,
	STATIC_DATASETS.communardo,
	STATIC_DATASETS.reddit,
	STATIC_DATASETS.procoreLargest,
	STATIC_DATASETS.helloFresh,
	STATIC_DATASETS.workday,
	STATIC_DATASETS.workdayLargest,
];

/**
 * Static collaboration-graph response served by `CollaborationUsersAtom`
 * whenever the debug panel is in "static dataset" mode. The same five
 * recommended users are returned for every static dataset — the metrics
 * payload swaps per organisation, but the collaborators stay constant so
 * that demo flows always show the same recognisable Teamwork Graph team.
 */
export const STATIC_COLLABORATION_USERS: ReadonlyArray<CollabGraphRecommendedEntity> = [
	{
		score: 0.5758,
		id: 'ari:cloud:identity::user/712020:fe8c7aeb-92b9-4075-bfed-e4d859bb587f',
		expand: {
			ari: 'ari:cloud:identity::user/712020:fe8c7aeb-92b9-4075-bfed-e4d859bb587f',
			account_id: '712020:fe8c7aeb-92b9-4075-bfed-e4d859bb587f',
			email: 'acherk@atlassian.com',
			name: 'Alvin Cherk',
			picture:
				'https://secure.gravatar.com/avatar/527c98c2e4b0cf1c9a2bc979886644a4?d=https%3A%2F%2Favatar-management--avatars.us-west-2.prod.public.atl-paas.net%2Finitials%2FAC-6.png',
		},
	},
	{
		score: 0.3358,
		id: 'ari:cloud:identity::user/70121:f604320b-d57f-4347-9af4-6134863803c2',
		expand: {
			ari: 'ari:cloud:identity::user/70121:f604320b-d57f-4347-9af4-6134863803c2',
			account_id: '70121:f604320b-d57f-4347-9af4-6134863803c2',
			email: 'hshahid@atlassian.com',
			name: 'Hamza Shahid',
			picture:
				'https://avatar-management--avatars.us-west-2.prod.public.atl-paas.net/70121:f604320b-d57f-4347-9af4-6134863803c2/371838a1-9613-4320-8e6e-b0542872590a/128',
		},
	},
	{
		score: 0.3259,
		id: 'ari:cloud:identity::user/63749bac9960988ef6bc6096',
		expand: {
			ari: 'ari:cloud:identity::user/63749bac9960988ef6bc6096',
			account_id: '63749bac9960988ef6bc6096',
			email: 'efernando@atlassian.com',
			name: 'Eve Fernando',
			picture:
				'https://avatar-management--avatars.us-west-2.prod.public.atl-paas.net/63749bac9960988ef6bc6096/835e5c9d-bb4d-4976-8408-03419a49b5c1/128',
		},
	},
	{
		score: 0.3024,
		id: 'ari:cloud:identity::user/712020:538b5dc0-5d53-4703-95be-0803cdd14327',
		expand: {
			ari: 'ari:cloud:identity::user/712020:538b5dc0-5d53-4703-95be-0803cdd14327',
			account_id: '712020:538b5dc0-5d53-4703-95be-0803cdd14327',
			email: 'shaq@atlassian.com',
			name: 'Shan Haq',
			picture:
				'https://avatar-management--avatars.us-west-2.prod.public.atl-paas.net/712020:538b5dc0-5d53-4703-95be-0803cdd14327/47074604-003e-4efd-a650-4b14283b5a5f/128',
		},
	},
	{
		score: 0.2296,
		id: 'ari:cloud:identity::user/63749bf65fce844d606712e6',
		expand: {
			ari: 'ari:cloud:identity::user/63749bf65fce844d606712e6',
			account_id: '63749bf65fce844d606712e6',
			email: 'ndutta2@atlassian.com',
			name: 'Neel Dutta',
			picture:
				'https://avatar-management--avatars.us-west-2.prod.public.atl-paas.net/63749bf65fce844d606712e6/c8f3eb84-f065-4e52-92c5-baac27ed7e6f/128',
		},
	},
];
